import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { ShoppingCart, X } from "lucide-react";
import { useCart } from "@/contexts/CartContext";
import { Card, CardContent } from "@/components/ui/card";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useCurrencyFormat } from "@/hooks/useCurrencyFormat";
import { Skeleton } from "@/components/ui/skeleton";

export const CartDrawer = () => {
  const { items, removeItem, clearCart, itemCount } = useCart();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { formatPrice, isLoading } = useCurrencyFormat();

  return (
    <Sheet>
      <SheetTrigger asChild>
        <Button variant="ghost" size="icon" className="relative text-primary hover:text-primary/80" aria-label={`${t("cart.title")} (${itemCount})`}>
          <ShoppingCart className="h-5 w-5" />
          <span className="absolute -top-1 -right-1 bg-primary text-primary-foreground text-xs rounded-full h-5 w-5 flex items-center justify-center font-bold">
            {itemCount}
          </span>
        </Button>
      </SheetTrigger>
      <SheetContent className="bg-background border-border w-[400px] sm:w-[540px]">
        <SheetHeader>
          <SheetTitle className="text-foreground">{t("cart.title")}</SheetTitle>
        </SheetHeader>
        <div className="mt-8 space-y-4">
          {items.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">{t("cart.empty")}</p>
          ) : (
            <>
              {items.map((item) => (
                <Card key={item.id} className="bg-card border-border">
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <h3 className="font-bold text-foreground">{item.name}</h3>
                        <p className="text-sm text-muted-foreground">{item.duration}</p>
                        {item.packType && (
                          <p className="text-sm text-muted-foreground">{item.packType}</p>
                        )}
                        <p className="text-sm text-muted-foreground mt-1">{t("cart.quantity")}: {item.quantity}</p>
                        <p className="text-lg font-bold text-primary mt-2">
                          {isLoading ? (
                            <Skeleton className="h-6 w-20" />
                          ) : (
                            formatPrice(item.price)
                          )}
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => removeItem(item.id)}
                        aria-label={t("cart.removeItem", { name: item.name, defaultValue: `Remove ${item.name}` })}
                        className="text-muted-foreground hover:text-foreground"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
              <div className="pt-4 space-y-2">
                <Button onClick={clearCart} variant="outline" className="w-full">
                  {t("cart.clearCart")}
                </Button>
                <Button 
                  className="w-full shadow-accent-glow"
                  onClick={() => navigate('/checkout')}
                >
                  {t("cart.continueToCheckout")}
                </Button>
              </div>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};
